import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";
import Paper from "@mui/material/Paper";
import { useFetchGetV2 } from "hooks/fetch/useFetchGetV2";
import { SettingsSection } from "components/Settings/SettingsSection";
import { useFormik } from "formik";
import * as yup from "yup";
import { TextField } from "components/TextField";
import { styled } from "@mui/material/styles";
import { useContext, useEffect, useMemo, useState } from "react";
import { useFetchPostV2 } from "hooks/fetch/useFetchPostV2";
import { SnackbarQueueContext } from "contexts/SnackbarQueueContext";
import { CustomPrompt } from "components/CustomPrompt";

const uuidMatcher = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
// eslint-disable-next-line no-template-curly-in-string
const uuidErrorMessage = "${path} must be a valid UUID";

const validationSchema = yup.object({
  workspaceId: yup
    .string()
    .label("Workspace ID")
    .matches(uuidMatcher, { message: uuidErrorMessage })
    .required(),
  profileId: yup
    .string()
    .label("Profile ID")
    .matches(uuidMatcher, { message: uuidErrorMessage })
    .required(),
  defaultReportId: yup
    .string()
    .label("Default Report ID")
    .matches(uuidMatcher, { message: uuidErrorMessage })
    .required()
});

export const PowerBISettingsSection = () => {
  const { addSnackbarToQueue } = useContext(SnackbarQueueContext);
  const [errorMessage, setErrorMessage] = useState();

  const {
    loading: getLoading,
    error: getError,
    response,
    refetch: refetchPowerBI
  } = useFetchGetV2("settings/powerbi");
  const {
    loading: postLoading,
    error: postError,
    postCallback: powerbiPost
  } = useFetchPostV2("settings/powerbi");

  const formik = useFormik({
    initialValues: {
      workspaceId: "",
      profileId: "",
      defaultReportId: ""
    },
    validationSchema,
    onSubmit: (values, { setSubmitting }) => {
      setErrorMessage();
      powerbiPost({
        workspace_id: values.workspaceId,
        profile_id: values.profileId,
        default_report_id: values.defaultReportId
      })
        .then(
          _success => {
            refetchPowerBI().catch(() => {
              addSnackbarToQueue({
                message: "Failed to refresh PowerBI configuration.",
                severity: "error"
              });
            });
            addSnackbarToQueue({
              message: "PowerBI Configuration updated.",
              severity: "success"
            });
          },
          err => {
            // need to get the error message from the response and save it to state so that we can show it on screen
            err.json().then(body => setErrorMessage(body?.detail));
            addSnackbarToQueue({
              message: "PowerBI Configuration failed to save.",
              severity: "error"
            });
          }
        )
        .finally(() => {
          setSubmitting(false);
        });
    }
  });

  // Need to keep the form values up to date with the response from the api
  useEffect(() => {
    formik.setValues({
      workspaceId: response?.workspace_id ?? "",
      profileId: response?.profile_id ?? "",
      defaultReportId: response?.default_report_id ?? ""
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [response]);

  // helper variable to see if any field has been changed
  const hasChanged = useMemo(() => {
    /** When page load prevent condition is always true */
    if (!response) {
      return false;
    }
    return (
      formik.values.workspaceId !== response?.workspace_id ||
      formik.values.profileId !== response?.profile_id ||
      formik.values.defaultReportId !== response?.default_report_id
    );
  }, [formik.values, response]);

  const loading = getLoading || postLoading;
  const error = getError || postError;

  return (
    <>
      <CustomPrompt
        shouldBlockNavigation={hasChanged}
        content={
          "You have unsaved changes in PowerBI Configuration. Are you sure you want to navigate away?"
        }
      />
      {/* inputProps={{ "aria-label": "prompt" }} */}
      <SettingsSection title="PowerBI Configuration">
        <form onSubmit={formik.handleSubmit} noValidate>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell data-col={1}>Name</TableCell>
                  <TableCell data-col={2}>Value</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell data-col={1}>Workspace ID</TableCell>
                  <TableCell data-col={2}>
                    <FixedWidthTextField
                      id="workspaceId"
                      name="workspaceId"
                      label="Workspace ID"
                      size="small"
                      required
                      value={formik.values.workspaceId}
                      fullWidth
                      inputProps={{ maxLength: 36 }}
                      error={
                        formik.touched.workspaceId &&
                        Boolean(formik.errors.workspaceId)
                      }
                      helperText={
                        formik.touched.workspaceId && formik.errors.workspaceId
                      }
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      disabled={loading}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell data-col={1}>Profile ID</TableCell>
                  <TableCell data-col={2}>
                    <FixedWidthTextField
                      id="profileId"
                      name="profileId"
                      label="Profile ID"
                      size="small"
                      required
                      value={formik.values.profileId}
                      inputProps={{ maxLength: 36 }}
                      fullWidth
                      error={
                        formik.touched.profileId &&
                        Boolean(formik.errors.profileId)
                      }
                      helperText={
                        formik.touched.profileId && formik.errors.profileId
                      }
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      disabled={loading}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell data-col={1}>Default Report ID</TableCell>
                  <TableCell data-col={2}>
                    <FixedWidthTextField
                      id="defaultReportId"
                      name="defaultReportId"
                      label="Default Report ID"
                      size="small"
                      required
                      value={formik.values.defaultReportId}
                      inputProps={{ maxLength: 36 }}
                      fullWidth
                      error={
                        formik.touched.defaultReportId &&
                        Boolean(formik.errors.defaultReportId)
                      }
                      helperText={
                        formik.touched.defaultReportId &&
                        formik.errors.defaultReportId
                      }
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      disabled={loading}
                    />
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          <ButtonWrapper>
            {error && (
              <Typography color="error" aria-label="error message">
                {errorMessage ?? "An error occurred while trying to save"}
              </Typography>
            )}
            <Button
              type="submit"
              color="primary"
              variant="contained"
              disabled={
                loading || !hasChanged || !formik.isValid || formik.isSubmitting
              }
            >
              Save
            </Button>
          </ButtonWrapper>
        </form>
      </SettingsSection>
    </>
  );
};

const ButtonWrapper = styled("div")`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: ${({ theme }) => theme.spacing(1)};
  gap: ${({ theme }) => theme.spacing(1)};
`;

const FixedWidthTextField = styled(TextField)`
  max-width: 400px;
`;
